import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Header } from "./components/Header";
import { QuadrantLegend } from "./components/QuadrantLegend";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Lazy load heavy components for better initial load time
const QuadrantChart = lazy(() =>
  import("./components/QuadrantChart").then((m) => ({
    default: m.QuadrantChart,
  })),
);
const DetailDrawer = lazy(() =>
  import("./components/DetailDrawer").then((m) => ({
    default: m.DetailDrawer,
  })),
);
const AlgorithmDocs = lazy(() =>
  import("./components/AlgorithmDocs").then((m) => ({
    default: m.AlgorithmDocs,
  })),
);

// Loading fallback component
function ChartLoadingFallback() {
  const { t } = useTranslation();

  return (
    <div className="w-full h-full min-h-[400px] flex items-center justify-center bg-gray-50 rounded-xl">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        <span className="text-sm text-gray-500">{t("common.loading")}</span>
      </div>
    </div>
  );
}

function App() {
  const { t } = useTranslation();

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <Header />

        {/* Main content */}
        <main className="max-w-screen-2xl mx-auto p-6">
          {/* Page title */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {t("chart.quadrantTitle")}
            </h2>
            <p className="text-muted-foreground">
              {t("chart.quadrantSubtitle")}
            </p>
          </div>

          {/* Legend & Filters */}
          <div className="mb-6">
            <QuadrantLegend />
          </div>

          {/* Quadrant Chart - Lazy loaded */}
          <div
            className="bg-card border border-border rounded-xl overflow-hidden shadow-sm"
            style={{ height: "calc(100vh - 320px)", minHeight: "500px" }}
          >
            <Suspense fallback={<ChartLoadingFallback />}>
              <QuadrantChart />
            </Suspense>
          </div>
        </main>

        {/* Algorithm Documentation - Lazy loaded */}
        <Suspense fallback={null}>
          <AlgorithmDocs />
        </Suspense>

        {/* Detail Drawer - Lazy loaded */}
        <Suspense fallback={null}>
          <DetailDrawer />
        </Suspense>
      </div>
    </ErrorBoundary>
  );
}

export default App;
